<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import Menu from 'primevue/menu'
import { useAuthStore } from '@/stores/auth'
import { roleLabel } from '@/utils/roles'
import { useNavigation } from '@/composables/useNavigation'
import { useBreakpoint } from '@/composables/useBreakpoint'

/** Superficie pública mínima del `Menu` de PrimeVue que usamos desde la plantilla. */
type MenuToggle = { toggle: (event: Event, target?: unknown) => void }

interface Props {
  layout: 'mobile' | 'tablet' | 'desktop'
  userMenuItems: object[]
}

const props = defineProps<Props>()

const router = useRouter()
const authStore = useAuthStore()
const breakpoint = useBreakpoint()
const { navItems, canSee, isActive } = useNavigation()

const userMenu = ref<MenuToggle | null>(null)

/** En el menú lateral el nombre ya se ve; repetirlo en la barra sería ruido. */
const showNavLabels = computed(() => breakpoint.value === 'desktop')

/**
 * Secciones que el usuario puede ver.
 *
 * Se filtran aquí y no con un `v-if` sobre el `v-for`: en Vue 3 el `v-if` se
 * evalúa antes que el `v-for`, así que el elemento todavía no existiría. Y
 * filtrar de verdad, en vez de esconder con `v-show`, importa: un enlace
 * oculto con `display: none` sigue en el DOM y un lector de pantalla lo lee
 * como si se pudiera ir allí.
 */
const visibleNavItems = computed(() => navItems.value.filter((item) => canSee(item)))
</script>

<template>
  <header
    class="no-print fixed inset-x-0 top-0 z-[var(--z-sticky)] h-16 border-b border-surface-200 bg-white/95 backdrop-blur dark:border-surface-700 dark:bg-surface-900/95"
    role="banner"
  >
    <div class="mx-auto flex h-full w-full max-w-screen-2xl items-center gap-3 px-4 sm:gap-4 sm:px-6">
      <!--
        Aquí no hay botón de menú.

        Existía uno que abría un cajón lateral, pero el menú ya está en la barra de
        abajo en móvil y tablet, y en escritorio está en los enlaces de esta misma
        barra. El botón, además, estaba muerto en móvil: la barra lateral no se
        monta en móvil, así que pulsarlo no hacía nada —se veía, se pulsaba y no
        pasaba nada—. Un control que no hace nada es peor que no tenerlo, porque
        hace perder el tiempo a quien lo prueba.

        La marca sí lleva su nombre en móvil y tablet. Antes solo aparecía a partir
        de `sm`, con lo que en móvil la barra superior no decía qué aplicación era;
        ahora que el hueco está libre, el nombre se ve siempre y la barra queda
        reducida a lo que sirve: dónde estás y quién eres.
      -->
      <!-- Marca: enlace real, para que se pueda abrir en pestaña nueva. -->
      <router-link
        to="/"
        class="flex shrink-0 items-center gap-2.5 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
        aria-label="COTIZADOR VRO, ir al inicio"
      >
        <span class="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-600 text-white">
          <i
            class="pi pi-briefcase text-lg"
            aria-hidden="true"
          />
        </span>
        <span class="text-base font-semibold tracking-tight text-surface-900 dark:text-surface-100">
          COTIZADOR VRO
        </span>
      </router-link>

      <!--
        Navegación: enlaces y no botones.

        Un botón de navegación no es un botón: no tiene `href`, así que no se
        puede abrir en pestaña nueva con el botón central, no se puede arrastrar
        ni copiar, y un lector de pantalla lo anuncia como una acción en vez de
        como el lugar donde estás. Además el estilo de `Button` competía con el
        de la barra y el resultado era un botón grande en medio de un menú.
      -->
      <nav
        v-if="showNavLabels"
        class="ml-2 hidden min-w-0 flex-1 items-center gap-1 overflow-x-auto lg:flex"
        aria-label="Navegación principal"
      >
        <!--
          Icono arriba y texto abajo.

          Con la sección de Ventas, los enlaces horizontales ya no cabían a
          1024 px: el último quedaba fuera de la barra. La disposición vertical
          aprovecha el alto que la barra ya tiene y reduce el ancho de cada
          enlace a su palabra más larga, no a icono + texto en fila.

          `overflow-x-auto` sigue siendo la red de seguridad para un futuro
          enlace que no quepa: los elementos no se encogen, así que sin esto se
          saldrían de la pantalla sin forma de llegar a ellos.
        -->
        <router-link
          v-for="item in visibleNavItems"
          :key="item.route"
          :to="{ name: item.route }"
          class="flex shrink-0 flex-col items-center justify-center gap-0.5 rounded-lg px-1.5 py-1 text-[11px] font-medium leading-none transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 xl:px-2"
          :class="isActive(item.route)
            ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-200'
            : 'text-surface-600 hover:bg-surface-100 hover:text-surface-900 dark:text-surface-300 dark:hover:bg-surface-800 dark:hover:text-surface-100'"
          :aria-current="isActive(item.route) ? 'page' : undefined"
        >
          <i
            :class="item.icon"
            class="text-sm"
            aria-hidden="true"
          />
          <span class="whitespace-nowrap">{{ item.label }}</span>
        </router-link>
      </nav>

      <div class="flex-1 lg:hidden" />

      <!-- Menú de usuario. -->
      <template v-if="authStore.user">
        <button
          type="button"
          class="flex shrink-0 items-center gap-2 rounded-lg py-1.5 pl-1.5 pr-2 transition-colors hover:bg-surface-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:hover:bg-surface-800"
          :aria-label="`Menú de ${authStore.user.name}`"
          aria-haspopup="menu"
          @click="userMenu?.toggle($event)"
        >
          <span
            class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-100 text-xs font-semibold text-primary-700 dark:bg-primary-900/40 dark:text-primary-200"
            aria-hidden="true"
          >
            {{ authStore.user.name.slice(0, 1).toUpperCase() }}
          </span>
          <!--
            El nombre y el rol solo aparecen a partir de `xl`.

            Entre 1024 y 1279 px la barra tiene que repartir su ancho entre la
            marca, diez secciones y el usuario. El avatar con la inicial y su
            `aria-label` siguen identificando la cuenta; el nombre completo
            vuelve en cuanto hay espacio.
          -->
          <span class="hidden min-w-0 text-left xl:block">
            <span class="block max-w-32 truncate text-sm font-medium text-surface-900 dark:text-surface-100">
              {{ authStore.user.name }}
            </span>
            <span class="block text-xs text-surface-500 dark:text-surface-400">
              {{ roleLabel(authStore.user?.roles[0] ?? '') || 'Usuario' }}
            </span>
          </span>
          <i
            class="pi pi-chevron-down text-xs text-surface-400"
            aria-hidden="true"
          />
        </button>

        <!--
          El menú lleva su propio `pt` porque el que trae el tema aprieta los
          elementos contra el borde y en una ventana tan baja se leía como una
          lista sin relleno. El ancho mínimo evita que en móvil se convierta en
          una tira estrecha pegada al borde.
        -->
        <Menu
          ref="userMenu"
          :model="props.userMenuItems"
          :popup="true"
          :append-to="props.layout === 'mobile' ? 'body' : undefined"
          :pt="{
            root: { class: 'min-w-56' },
            list: { class: 'p-2' },
            item: { class: 'px-3 py-2.5 gap-3 rounded-lg' },
            itemIcon: { class: 'text-surface-500 dark:text-surface-400' },
            itemLabel: { class: 'text-sm' },
            separator: { class: 'my-2' },
          }"
        />
      </template>

      <button
        v-else
        type="button"
        class="shrink-0 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-surface-900"
        @click="router.push({ name: 'login' })"
      >
        Iniciar sesión
      </button>
    </div>
  </header>
</template>
